import { BookOpen, CalendarClock } from "lucide-react";
import { CardShell, ScheduledBadge, parsePaths, type MetaFact } from "./CardShell";
import { isScheduled, scheduledDateLabel } from "../scheduled";
import { langPath, type Dict, type Lang } from "@/lib/i18n";
import { formatLessons } from "@/lib/format";
import { modulePoster } from "@/lib/lecturehub/poster";
import { moduleHref } from "../moduleHref";
import type { Module } from "@/lib/lecturehub/types";
import { isFreeToUnlock } from "../paywall";

export function CourseCard({
  lang,
  t,
  module: m,
  enrolled = false,
  cfHash = null,
  library = false,
}: {
  lang: Lang;
  t: Dict;
  module: Module;
  enrolled?: boolean;
  cfHash?: string | null;
  library?: boolean;
}) {
  // The backend's own published-lesson count. The card used to walk the whole
  // curriculum to derive this, which is why every listing shipped a lessons
  // tree; verified identical on 18 of 18 live courses (2026-09-14).
  const lessonCount = m.lessonsTotalCount ?? null;
  const paths = parsePaths(m.keywords, lang);

  // An enrolled card opens the course's learn root, which resolves the first
  // published lesson and redirects (learn/[id]/[slug]/page.tsx). Resolving it
  // here instead meant fetching the curriculum for every card on every listing
  // to build a link most visitors never click.
  const openHref = enrolled
    ? langPath(lang, `/learn/${m.id}/${m.slug ?? ""}`)
    : moduleHref(lang, m, library);

  const scheduled = isScheduled(m);
  const scheduledLabel = scheduledDateLabel(t.detail, m.publishAt, lang);

  const facts: MetaFact[] = [];
  if (scheduledLabel) {
    facts.push({ Icon: CalendarClock, label: scheduledLabel });
  }
  if (lessonCount != null) {
    facts.push({ Icon: BookOpen, label: formatLessons(lessonCount, lang, t.units) });
  }

  return (
    <CardShell
      t={t}
      href={moduleHref(lang, m, library)}
      openHref={openHref}
      poster={modulePoster(m, cfHash)}
      posterFallback={t.detail.typeCourse}
      typeLabel={t.detail.typeCourse}
      TypeIcon={BookOpen}
      statusLabel={scheduled ? undefined : isFreeToUnlock(m) ? t.detail.free : undefined}
      posterBadge={scheduled ? <ScheduledBadge t={t} /> : undefined}
      title={m.name ?? "—"}
      abstract={m.abstract}
      enrolled={enrolled}
      paths={paths}
      facts={facts}
      ctaLabel={enrolled ? t.detail.continueCta : t.detail.enroll}
    />
  );
}
